import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

/**
 * オレンジ帯の見出し（白文字SVG）を左からスライドインさせる
 *
 * @description Figma（1156:12051）のモーションを再現する。
 *              [data-ttl-slide] が overflow:hidden のマスク、その直下の子が1行分。
 *              各行はマスクの左外から本来の位置まで滑り込み、2行目以降は少し遅れて動く。
 *              ※ gsap.from を使うため、JSが動かない環境では見出しはそのまま表示される。
 */

/**
 * CSSのcubic-bezier()と同じイージング関数を作る
 * @param {number} x1 - 制御点1のx
 * @param {number} y1 - 制御点1のy
 * @param {number} x2 - 制御点2のx
 * @param {number} y2 - 制御点2のy
 * @returns {(progress: number) => number} 進捗0〜1を受け取り、補間後の値を返す関数
 * @description GSAPのCustomEaseでも同じことができるが、この1本のためだけに
 *              プラグイン（min後で約5KB）を読むのは重いので自前で持つ。
 * @example
 * gsap.to(el, { x: 0, ease: cubicBezier(0.16, 1, 0.3, 1) })
 */
const cubicBezier = (x1: number, y1: number, x2: number, y2: number) => {
  /**
   * 3次ベジェ曲線の片軸ぶんの値を返す（始点0・終点1固定）
   * @param {number} t - 媒介変数
   * @param {number} a - 制御点1の座標
   * @param {number} b - 制御点2の座標
   * @returns {number} tにおける座標
   */
  const axis = (t: number, a: number, b: number): number => {
    const u = 1 - t

    return 3 * u * u * t * a + 3 * u * t * t * b + t * t * t
  }

  return (progress: number): number => {
    // x が progress になる t を二分探索で求めてから y を返す
    let low = 0
    let high = 1
    let t = progress

    for (let i = 0; i < 20; i += 1) {
      const x = axis(t, x1, x2)

      if (Math.abs(x - progress) < 1e-5) {
        break
      }

      if (x < progress) {
        low = t
      } else {
        high = t
      }

      t = (low + high) / 2
    }

    return axis(t, y1, y2)
  }
}

/** Figma: cubic-bezier(0.16, 1, 0.3, 1) */
const EASE = cubicBezier(0.16, 1, 0.3, 1)

/** Figma: 1行目450ms / 2行目470ms。差は体感できないので揃える */
const DURATION = 0.47

/** Figma: 2行目は80ms遅れて動き出す */
const STAGGER = 0.08

/** Figma: マスク左端の 9.65px 外から始まる。端数は切りよく10pxにする */
const OFFSET = 10

/** 動きを減らす設定のユーザーには適用しない */
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * 1つの見出しマスクにスライドインを設定する
 * @param {HTMLElement} mask - [data-ttl-slide] を持つマスク要素
 */
const slideIn = (mask: HTMLElement): void => {
  const rows = Array.from(mask.children) as HTMLElement[]

  if (rows.length === 0) {
    return
  }

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: mask,
      start: "top 88%",
      // once: true は使わない（理由は src/js/module/comp-scroll-reveal.ts のコメント参照）。
      // 既定の toggleActions（play none none none）で「1回再生・巻き戻さない」になる。
      // 画像の読み込みで幅が変わってもスタート位置を取り直せるようにする
      invalidateOnRefresh: true,
    },
  })

  rows.forEach((row, index) => {
    const at = index * STAGGER

    tl.from(
      row,
      {
        // 行ごとの幅ではなくマスク幅ぶん動かす（Figmaは全行が同じ距離だけ動く）
        x: () => -(mask.offsetWidth + OFFSET),
        duration: DURATION,
        ease: EASE,
      },
      at,
    )
      // Figmaは動き出しと同時にパッと出す。マスクが効かない環境での保険も兼ねる
      .from(row, { autoAlpha: 0, duration: 0.05 }, at)
  })
}

if (!reduceMotion) {
  document.querySelectorAll<HTMLElement>("[data-ttl-slide]").forEach(slideIn)
}
